import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await axios.get(`${API}/resources`);
        setResources(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, []);

  const handleDownload = async (resourceId, fileName) => {
    try {
      const response = await axios.get(`${API}/resources/${resourceId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ivory"><p className="label-editorial">Loading</p></div>;

  return (
    <div className="min-h-screen bg-ivory" data-testid="resources-page">
      <section className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-editorial mb-4">Resources</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-tight text-charcoal" data-testid="resources-title">
              Tools for the <em className="text-burgundy not-italic">journey</em>.
            </h1>
            <p className="text-lg text-charcoal-muted max-w-2xl mx-auto mt-6">
              Practical templates, guides, and workbooks — curated by and for the sisterhood.
            </p>
          </div>

          {resources.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-subtle">
              {resources.map((resource) => (
                <div
                  key={resource.resource_id}
                  className="p-8 border border-subtle -m-px bg-white editorial-card"
                  data-testid={`resource-${resource.resource_id}`}
                >
                  <p className="label-editorial mb-4">{resource.category}</p>
                  <h3 className="font-serif text-2xl text-charcoal mb-3 leading-snug">{resource.title}</h3>
                  <p className="text-sm text-charcoal-muted mb-6 leading-relaxed">{resource.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-subtle">
                    <p className="text-xs text-muted-warm font-mono">{(resource.file_size / 1024).toFixed(0)} KB · {resource.download_count} downloads</p>
                    <button
                      onClick={() => handleDownload(resource.resource_id, resource.file_name)}
                      className="text-burgundy hover:text-charcoal transition-colors inline-flex items-center gap-2 text-sm font-medium"
                      data-testid={`download-${resource.resource_id}`}
                    >
                      <Download className="h-4 w-4" /> Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-charcoal-muted text-lg">Resources coming soon.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Resources;
